import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { APIData, PopupData, Service } from 'webapp-types';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class AccountWhiteLabelingPopupPromoComponent extends PopupInnerModalComponent<PopupData.Billing.WhitelabelingPromo> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('tour') declare tourService: Service.Tour;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.tourService.initTour(TourId.WHITE_LABELING_PAYING_CUSTOMERS_PROMO)?.markDone();
		this.analyticsService.event(TrackingEvent.WL_POPUP_SHOWN);
	}

	get company(): APIData.Account.WhiteLabelingPayingCustomersPromoCompany {
		return this.popupData.company;
	}

	get companyLogo(): string {
		return this.company.logo || '/images/white-labeling/popup-promo/company-logo.svg';
	}

	get companyName(): string {
		return this.company.name || 'My company';
	}

	get companyDomain(): string {
		return this.company.domain || 'mycompany.com';
	}

	get loginEmail(): string {
		return `me@${this.companyDomain}`;
	}

	get canRequestSetupHelp(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	@action
	transitToWhitelabelingPage(): void {
		this.accountService.openAccountAppWhitelabeling();
		this.analyticsService.event(TrackingEvent.WL_POPUP_COMPLETED, { choice: 'start' });
		this.close();
	}

	@action
	requestSetupHelp(): void {
		this.accountService.requestSetupHelp();
		this.analyticsService.event(TrackingEvent.CALL_INITIATED, { origin: 'white_labeling' });
		this.analyticsService.event(TrackingEvent.WL_POPUP_COMPLETED, { choice: 'help' });
	}
	<template>
		<div class="content">
			<h2>Re-brand Databox and have your own analytics tool</h2>
			<p>
				Replace the Databox brand with your own. Share fully customized dashboards,
				performance alerts, emails, and more with clients or internal teams. Don’t
				worry—we’ll let you take the credit.
			</p>

			<div class="options">
				<div class="option">
					<div class="option__image">
						<img
							src="/images/white-labeling/popup-promo/login.svg"
							height="177"
							width="auto"
							alt
						/>
						<div class="login-header">
							<img src={{this.companyLogo}} height="18" width="18" alt />
							<span>{{this.companyName}}</span>
						</div>
						<div class="login-email">{{this.loginEmail}}</div>
					</div>
					<div class="option__title">
						Display your company logo on the login page
					</div>
				</div>
				<div class="option">
					<div class="option__image">
						<img
							src="/images/white-labeling/popup-promo/subdomain.svg"
							height="177"
							width="auto"
							alt
						/>
						<div class="browser-tab">
							<img src={{this.companyLogo}} height="10" width="10" alt />
							<span>{{this.companyName}}</span>
						</div>
						<div class="browser-search">{{this.companyDomain}}</div>
					</div>
					<div class="option__title">
						Replace the app logo & select a custom subdomain
					</div>
				</div>
				<div class="option">
					<div class="option__image">
						<img
							src="/images/white-labeling/popup-promo/email.svg"
							height="177"
							width="auto"
							alt
						/>
						<div class="email-header">
							<img src={{this.companyLogo}} height="18" width="18" alt />
							<span>{{this.companyName}}</span>
						</div>
					</div>
					<div class="option__title">
						Create branded, custom emails for notifications & alerts
					</div>
				</div>
			</div>

			<div class="controls2 controls2--center">
				<SubmitButtonComponent
					@text="Start white labeling"
					@extraClass="save"
					@size="big"
					@noDelay={{true}}
					@onTrigger={{this.transitToWhitelabelingPage}}
				/>
				{{#if this.canRequestSetupHelp}}
					<SubmitButtonComponent
						@text="Need help setting up?"
						@extraClass="theme-blue-outlined"
						@size="big"
						@noDelay={{true}}
						@onTrigger={{this.requestSetupHelp}}
					/>
				{{/if}}
			</div>
			<div class="learn-more">
				<a href="https://help.databox.com/how-to-white-label-databox" target="_blank">
					Learn more
					<CommonUiIconComponent @name="External" @size="sm" />
				</a>
			</div>
		</div>
	</template>
}
